import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode";
import { useAuthentication } from "@/composables/authentication";
import { AccountInfo } from "@azure/msal-browser";
import { computed, ref } from "vue";
import { UserInfo } from "@/models/auth/userInfo";
import { RoleType } from "@/constants/Roles";

export const useAuthStore = defineStore("auth", () => {
  const auth = useAuthentication();

  const user = ref<AccountInfo>();
  const roles = ref<string[]>([]);
  const issuedRoles = ref<string[]>([]);
  const token = ref("");
  const isAssumingRole = ref(false);
  const isAuthenticated = ref(false);

  const username = computed(() => user.value?.username);

  const assumedRole = computed(() => {
    if (roles.value && isAssumingRole && roles.value.length === 1) {
      // It is only possible to assume a single role so this is safe to do.
      return roles.value[0];
    }
    return undefined;
  });

  function isInSomeRole(rolesToCheckFor: RoleType[]): boolean {
    const userRoles = roles.value;
    if (userRoles) {
      return rolesToCheckFor.some((roleToCheck) =>
        userRoles.some((role) => role === roleToCheck),
      );
    }
    return false;
  }

  function isInRole(roleToCheck: RoleType): boolean {
    return isInSomeRole([roleToCheck]);
  }

  function assumeRole(role: string) {
    if (issuedRoles.value.includes(role)) {
      isAssumingRole.value = true;
      roles.value = [role];
    } else {
      throw Error("Cannot Assume role");
    }
  }

  function resetRoles() {
    isAssumingRole.value = false;
    roles.value = issuedRoles.value;
  }

  async function initializeAuthentication() {
    await auth.initializeAuthentication();
  }

  async function logOut() {
    await auth.signOut();
  }

  async function logIn() {
    await auth.signIn();
  }

  async function acquireTokenRedirect(scope: string) {
    const token = await auth.getTokenRedirect(scope);
    return token;
  }

  async function authenticate() {
    await auth.authenticate();
  }

  function setTokenInfo(accessToken: string) {
    const userInfo = jwtDecode<UserInfo>(accessToken);
    token.value = accessToken;

    if (!isAssumingRole.value) roles.value = userInfo.roles;
    issuedRoles.value = userInfo.roles;
    isAuthenticated.value = true;
  }

  return {
    user,
    username,
    isAuthenticated,
    assumedRole,
    issuedRoles,
    isAssumingRole,
    roles,

    isInSomeRole,
    isInRole,
    assumeRole,
    resetRoles,
    initializeAuthentication,
    logOut,
    logIn,
    acquireTokenRedirect,
    authenticate,
    setTokenInfo,
  };
});
